import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IImportSubmissionSummary, ImportRow } from '../../_core/Importer/importer.interface';
import { createStore } from '../../../lib/stores/create-store';
import { ParseError } from 'papaparse';
import moment from 'moment';

// Material UI
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

// Data
import { STEPS } from '../../_core/Importer/importer.constants';
import { importTemplateUrl } from '../../_core/Importer/importer.helpers';
import { getImportPlayersMessages } from './import-players.messages';
import { replaceKeyWithID, toHash } from '../../../lib/helpers/data-structure.helpers';

// Components
import { ImporterContainer } from '../../_core/Importer/importer.container';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { isValidEmail, isValidNumber, isValidPhone } from '../../../lib/helpers/validation.helpers';
import { ContactRelationshipValues, DEFAULT_DATE_OF_BIRTH_FALLBACK, PrimarySportValues, SportInvolvementValues, Sport, SportTerminology } from '../../../lib/types';
import { DateOfBirthOptionsField } from './date-of-birth-options-field.component';
import { DateOfBirthOptionsSummary } from './date-of-birth-options-summary.component';
import { JerseyRetentionChoice, JerseyRetentionOptionsField, jerseyRetentionAnswer } from './jersey-retention-options-field.component';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SPORT_TERMINOLOGY } from '../../../lib/constants';

const [usePlayerImportStep, , setPlayerImportStep] = createStore<number>(STEPS.INSTRUCTIONS);
const [usePlayerImportItems, , setPlayerImportItems] = createStore<ImportRow[]>([]);
const [usePlayerImportErrors, , setPlayerImportErrors] = createStore<ParseError[]>([]);

// Prefixes, so one entry per sport also covers the template's "(John)" / "(Doe)" example suffixes.
const firstNamePrefixes = Object.values(Sport).map(
  sport => SPORT_TERMINOLOGY[sport][SportTerminology.ATHLETE] + ' First Name'
);

const lastNamePrefixes = Object.values(Sport).map(
  sport => SPORT_TERMINOLOGY[sport][SportTerminology.ATHLETE] + ' Last Name'
);


interface IImportPlayersContainer {
  user?: IUser | null;
  createPlayerAssessments: (
    rows: any[],
    summary: IImportSubmissionSummary,
    fillMissingDateOfBirth?: DateOfBirthFallback,
    overwriteDateOfBirth?: boolean,
    retainJerseyAssignments?: boolean
  ) => void;
  createAgeGroup: Function;
  onImportError: (operation: string, error: unknown) => void;
  ageGroups: IAgeGroup[];
  positions: IPosition[];
  // Jersey retention is an assessment setting, so only members who can edit those are asked.
  canSetJerseyRetention?: boolean;
  currentlyRetainsJerseys?: boolean;
}

const JERSEY_COLUMN_KEYS = ['jerseyColour', 'jerseyNumber'];

export const ImportPlayersContainer: React.FC<IImportPlayersContainer> = ({
  user,
  createPlayerAssessments,
  createAgeGroup,
  onImportError,
  ageGroups,
  positions,
  canSetJerseyRetention = false,
  currentlyRetainsJerseys = false
}) => {
  const sport = useCompanySport()
  const isDefaultSport = sport === Sport.default

  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const messages = useMemo(() => getImportPlayersMessages(termLookup), [termLookup]);

  const step = usePlayerImportStep();
  const setStep = (step: number) => setPlayerImportStep(step);

  const importItems = usePlayerImportItems();
  const setImportItems = (items: ImportRow[]) => setPlayerImportItems(items);
  const errorItems = usePlayerImportErrors();
  const setImportErrors = (errors: ParseError[]) => setPlayerImportErrors(errors);

  // Chosen on the column-selection step: whether a row may arrive with no date of
  // birth, and what the server fills the blank with when one does.
  const [allowMissingDateOfBirth, setAllowMissingDateOfBirth] = useState(false);
  const [dateOfBirthFallback, setDateOfBirthFallback] = useState<DateOfBirthFallback>(DEFAULT_DATE_OF_BIRTH_FALLBACK);
  const [overwriteDateOfBirth, setOverwriteDateOfBirth] = useState(false);

  // Taking the date already on file and replacing it are opposite instructions, so the overwrite
  // cannot apply while that fallback is chosen.
  const canOverwriteDateOfBirth = !(allowMissingDateOfBirth && dateOfBirthFallback === 'DATE_ON_FILE');

  // Cleared, never suppressed at submit: the checkbox the operator sees and the value on the wire
  // are one, so an override that cannot apply can never ride along unseen.
  useEffect(() => {
    if (!canOverwriteDateOfBirth) setOverwriteDateOfBirth(false);
  }, [canOverwriteDateOfBirth]);

  const [jerseyRetention, setJerseyRetention] = useState<JerseyRetentionChoice>('UNCHANGED');
  const [mapsJerseys, setMapsJerseys] = useState(false);
  const handleMappedColumnsChange = useCallback(
    (columnKeys: string[]) => setMapsJerseys(columnKeys.some(key => JERSEY_COLUMN_KEYS.includes(key))),
    []
  );
  const asksJerseyRetention = canSetJerseyRetention && mapsJerseys;

  const resetImportOptions = useCallback(() => {
    setAllowMissingDateOfBirth(false);
    setDateOfBirthFallback(DEFAULT_DATE_OF_BIRTH_FALLBACK);
    setOverwriteDateOfBirth(false);
    setJerseyRetention('UNCHANGED');
  }, []);

  const handleSubmit = (rows: any[], summary: IImportSubmissionSummary) => {
    const currAgeGroupNameHash: { [key: string]: string } = toHash(ageGroups, 'name', '_id');
    const currPositionNameHash: { [key: string]: string } = toHash(positions, 'name', '_id');
    const toCreateAgeGroups: string[] = replaceKeyWithID(rows, currAgeGroupNameHash, 'ageGroup', 'ageGroupId');
    replaceKeyWithID(rows,  currPositionNameHash, 'position', 'positionId');

    const createAgeGroups = toCreateAgeGroups.map(name => createAgeGroup(name))

    const done = () => {
      const importRows: any[] = [];

      rows.forEach(row => {
        importRows.push({
          player: {
            ..._.omit(row, ['positionId', 'ageGroupId', 'dateOfBirth','jerseyColour','jerseyNumber', 'primarySport', 'contacts']),
            // Blank rather than absent: the wire requires the key, and the server reads a
            // blank as the missing date the override's fallback fills in.
            dateOfBirth: row.dateOfBirth?.replace(/[\\,.\-:]/g,'/') ?? '',
            primarySport: row.primarySport?.toLowerCase(),
            contacts: row.contacts?.map((c: {height?: string; relationship?: string; sportInvolvement?: string}) => ({
              ...c,
              height: c.height ? Number(c.height) : undefined,
              relationship: c.relationship?.toLowerCase(),
              sportInvolvement: c.sportInvolvement?.toLowerCase()
            }))
          },
          jerseyColour: row.jerseyColour,
          jerseyNumber: row.jerseyNumber ? Number(row.jerseyNumber) : undefined,
          positionId: row.positionId,
          ageGroupId: row.ageGroupId,
          assessmentId: user?.settings.activeAssessmentId,
        })
      })

      createPlayerAssessments(
        importRows,
        summary,
        allowMissingDateOfBirth ? dateOfBirthFallback : undefined,
        overwriteDateOfBirth,
        asksJerseyRetention ? jerseyRetentionAnswer(jerseyRetention) : undefined
      );
    }

    if (createAgeGroups.length) {
      // Create any missing AgeGroups and assign them to the rows
      Promise.all(createAgeGroups).then(createdAgeGroupResults => {
        const createdAgeGroups = createdAgeGroupResults.map(result => result.data?.createAgeGroup);
        const createdAgeGroupNameHash: { [key: string]: string } = toHash(createdAgeGroups, 'name', '_id');

        replaceKeyWithID(rows, createdAgeGroupNameHash, 'ageGroup', 'ageGroupId');
        done();
      }).catch((error: unknown) => onImportError('createAgeGroups', error));
    } else done()
  };

  const positionNames = useMemo(() => positions.map(({name}) => name), [positions])

  // Required or optional depending on the override, so it is declared once and placed
  // into whichever list the operator's choice puts it in.
  const dateOfBirthColumn = useMemo(() => ({
    key: 'dateOfBirth', label: 'Date of Birth', matchPrefixes: ['Date of Birth'],
    formatType: 'DATE', importFormat: 'YYYY/MM/DD', format: 'YYYY/MM/DD', validators: [
      (value: string, format?: string) => {
        // A blank is the operator's opt-in, not a bad date — the server fills it in.
        if (!value) return allowMissingDateOfBirth;

        const testValue = value.replace(/[\\,.\-:]/g,'/');
        let altFormat;

        switch(format) {
          case 'YYYY/MM/DD':
            altFormat = 'YYYY/M/D';
            break;
          case 'DD/MM/YYYY':
            altFormat = 'D/M/YYYY';
            break;
          case 'MM/DD/YYYY':
            altFormat = 'M/D/YYYY';
            break;
        }

        const parsed = [format, altFormat].map((candidate) => moment(testValue, candidate, true)).find((date) => date.isValid());

        // The server refuses a date of birth in the future, so the row is flagged before it is sent.
        return !!parsed && !parsed.isAfter(moment(), 'day');
      }
    ]
  }), [allowMissingDateOfBirth])

  // Declaration order is the display order: the matching and review steps group these ahead of
  // every optional column, and the downloadable CSV template leads with them in this order.
  const requiredColumns = useMemo(() => [
    { key: 'firstName', label: 'First Name', matchPrefixes: firstNamePrefixes },
    { key: 'lastName', label: 'Last Name', matchPrefixes: lastNamePrefixes },
    ...(allowMissingDateOfBirth ? [] : [dateOfBirthColumn]),
    { key: 'position', label: 'Position', matchPrefixes: ['Position'], description: "Must be one of: " + positionNames.join(", "), validators: [
        (value: string) => {
          return !!positionNames.find(name => name.toLowerCase() === value.toLowerCase())
        }
      ]
    },
    { key: 'ageGroup', label: 'Age Group', matchPrefixes: ['Age Group'] },
  ], [positionNames, allowMissingDateOfBirth, dateOfBirthColumn])

  // Memoized like requiredColumns: ImporterContainer folds all three lists into expectedColumns,
  // whose identity gates the matcher's pre-map effect and ImporterResults' column effect.
  const optionalColumns = useMemo(() => [
    ...(allowMissingDateOfBirth ? [dateOfBirthColumn] : []),
    { key: 'nickname', label: 'Nickname' },
    { key: 'gender', label: 'Gender', matchPrefixes: ['Gender'] },
    { key: 'previousPlacement', label: 'Previous Placement', matchAliases: ['Last Year'], matchPrefixes: ['Previous Placement'], hideInstructions: sport !== Sport.hockey },
    { key: 'externalId', label: 'External ID', matchPrefixes: ['Hockey Canada ID'] },
    { key: 'jerseyColour', label: 'Jersey Colour', description: 'Colour of jersey assigned for the year', matchAliases: ['Jersey Color'], hideInstructions: isDefaultSport },
    { key: 'jerseyNumber', label: 'Jersey Number', description: 'Number of jersey assigned for the year', matchAliases: ['Jersey #', 'Jersey Num', 'Jersey No.'], hideInstructions: isDefaultSport, validators: [
        (value: string) => {
          return !value || isValidNumber(value);
        }
      ]
    },
    { key: 'primarySport', label: 'Primary Sport', description: `Sport that the ${termLookup[SportTerminology.ATHLETE].toLowerCase()} primarily participates in`, hideInstructions: !isDefaultSport, validators: [
        (value: string) => {
          return !value || PrimarySportValues.includes(value.toLowerCase())
        }
      ]
    }
  ], [sport, isDefaultSport, termLookup, allowMissingDateOfBirth, dateOfBirthColumn]);

  const repeatedColumnSets = useMemo(() => [{
    name: 'Contact',
    key: 'contacts',
    columns: [
      { key: 'name', set: 'contacts', label: 'Contact Name', matchPrefixes: ['Parent Name', 'Mother Name', 'Father Name', 'Contact #1 Name', 'Contact #2 Name', 'Contact #3 Name'] },
      {
        key: 'phone',
        set: 'contacts',
        label: 'Contact Phone',
        matchPrefixes: ['Parent Phone', 'Mother Phone', 'Father Phone', 'Contact #1 Mobile Number', 'Contact #2 Mobile Number', 'Contact #3 Mobile Number'],
        validators: [
          (value: string) => {
            return isValidPhone(value);
          }
        ]
      },
      {
        key: 'email',
        set: 'contacts',
        label: 'Contact Email',
        matchPrefixes: ['Parent Email', 'Mother Email', 'Father Email', 'Contact #1 Email', 'Contact #2 Email', 'Contact #3 Email'],
        validators: [
          (value: string) => {
            return isValidEmail(value);
          }
        ],
      },
      {
        key: 'relationship',
        set: 'contacts',
        label: 'Contact Relationship',
        matchPrefixes: ['Parent Relationship', 'Mother Relationship', 'Father Relationship', 'Contact #1 Relationship', 'Contact #2 Relationship', 'Contact #3 Relationship'],
        validators: [
          (value: string) => {
            return !value || ContactRelationshipValues.includes(value.toLowerCase())
          }
        ],
      },
      {
        key: 'height',
        set: 'contacts',
        label: 'Contact Height',
        hideInstructions: !isDefaultSport,
        matchPrefixes: ['Parent Height', 'Mother Height', 'Father Height', 'Contact #1 Height', 'Contact #2 Height', 'Contact #3 Height'],
        validators: [
          (value: string) => {
            return isValidNumber(value);
          }
        ],
      },
      {
        key: 'sportInvolvement',
        set: 'contacts',
        label: 'Contact Involvement in Sport',
        hideInstructions: !isDefaultSport,
        matchPrefixes: ['Parent Involvement in Sport', 'Mother Involvement in Sport', 'Father Involvement in Sport', 'Contact #1 Involvement in Sport', 'Contact #2 Involvement in Sport', 'Contact #3 Involvement in Sport'],
        validators: [
          (value: string) => {
            return !value || SportInvolvementValues.includes(value.toLowerCase())
          }
        ],
      }
    ]
  }], [isDefaultSport]);

  return <ImporterContainer
    onSubmit={handleSubmit}
    onReset={resetImportOptions}
    onMappedColumnsChange={handleMappedColumnsChange}
    step={step}
    setStep={setStep}
    importItems={importItems}
    setImportItems={setImportItems}
    importErrors={errorItems}
    setImportErrors={setImportErrors}
    requiredColumns={requiredColumns}
    optionalColumns={optionalColumns}
    repeatedColumnSets={repeatedColumnSets}
    columnSelectionOptions={<>
      <DateOfBirthOptionsField
        termLookup={termLookup}
        allowMissing={allowMissingDateOfBirth}
        fallback={dateOfBirthFallback}
        overwrite={overwriteDateOfBirth}
        canOverwrite={canOverwriteDateOfBirth}
        setAllowMissing={setAllowMissingDateOfBirth}
        setFallback={setDateOfBirthFallback}
        setOverwrite={setOverwriteDateOfBirth}
      />
      {asksJerseyRetention && <JerseyRetentionOptionsField
        termLookup={termLookup}
        choice={jerseyRetention}
        currentlyRetained={currentlyRetainsJerseys}
        setChoice={setJerseyRetention}
      />}
    </>}
    dataReviewSummary={<DateOfBirthOptionsSummary
      termLookup={termLookup}
      allowMissing={allowMissingDateOfBirth}
      fallback={dateOfBirthFallback}
      overwrite={overwriteDateOfBirth}
    />}
    extensions={['csv']}
    showFileStructure
    instructionHeader={<>
      <Typography gutterBottom><IntlMsg {...messages.instructionsExisting} /></Typography>
      <Typography variant="subtitle2" align="center" style={{ marginTop: '16px' }}>
        <IntlMsg {...messages.templatePrompt} values={{
          link: <Link href={importTemplateUrl('player', sport)} download={`${athleteTerm} Import Template.csv`}>
            <IntlMsg {...messages.templateLink} />
          </Link>
        }} />
      </Typography>
    </>}
    instructionFooter={<Typography><IntlMsg {...messages.columnOrderNote} /></Typography>}
  />;
};
